import {
  either,
  equals,
  pathOr,
  isEmpty,
  isNil,
  has,
  propEq,
  pipe,
  replace,
  trim,
  toLower,
} from 'ramda'
import { formatDateUTC } from '../dateUtils'
import { capitalize } from '../wordProcessing'
import { isGroupProduct } from '../expenseUtils'
import { formatPriceCents } from '../formatPrice'

/**
 * When adding new transaction types
 *
 * 1. add the type to the constants below
 * 2a. add the constant to either the account or expense types list
 * 2b. if the value should be colored red, add it to the negative list
 * 3. add a switch case for that transaction type
 * 4. add a test in the spec file of the same name
 * 5. update the list test to account for the new type
 */

/*----- Transaction types -----------------------------------------------*/

// Account types (premiums)
const ACCOUNT_BACK_PAYMENT = 'ACCOUNT_BACK_PAYMENT'
const ACCOUNT_CANCELLATION_REFUND = 'ACCOUNT_CANCELLATION_REFUND'
const ACCOUNT_CREDIT_PAYMENT = 'ACCOUNT_CREDIT_PAYMENT'
const ACCOUNT_CREDIT_WITHDRAWAL = 'ACCOUNT_CREDIT_WITHDRAWAL'
const ACCOUNT_CREDIT_FROM_DEDUCTIBLE_TRANSFER =
  'ACCOUNT_CREDIT_FROM_DEDUCTIBLE_TRANSFER'
const ACCOUNT_ENROLLMENT_PAYMENT = 'ACCOUNT_ENROLLMENT_PAYMENT'
const ACCOUNT_FAILED_PAYMENT = 'ACCOUNT_FAILED_PAYMENT'
const ACCOUNT_MONTHLY_PAYMENT = 'ACCOUNT_MONTHLY_PAYMENT'
const ACCOUNT_PAYMENT = 'ACCOUNT_PAYMENT'
const ACCOUNT_REFUND = 'ACCOUNT_REFUND'
const ACCOUNT_REINSTATEMENT_PAYMENT = 'ACCOUNT_REINSTATEMENT_PAYMENT'
const COVERAGE_CANCELLATION = 'COVERAGE_CANCELLATION'
const COVERAGE_SUSPENSION = 'COVERAGE_SUSPENSION'
const INVITATION_CREDIT = 'INVITATION_CREDIT'

// Expense types
const ADJUSTMENT = 'ADJUSTMENT'
const DEDUCTIBLE_TRANSFER = 'DEDUCTIBLE_TRANSFER'
const EXPENSE_AUTOPAYMENT = 'EXPENSE_AUTOPAYMENT'
const EXPENSE_CALCULATION_FAILED = 'EXPENSE_CALCULATION_FAILED'
const EXPENSE_FAILED_PAYMENT = 'EXPENSE_FAILED_PAYMENT'
const EXPENSE_INITIAL_PAYMENT = 'EXPENSE_INITIAL_PAYMENT'
const EXPENSE_LATE_FEE = 'EXPENSE_LATE_FEE'
const EXPENSE_MERGED = 'EXPENSE_MERGED'
const EXPENSE_PAYMENT = 'EXPENSE_PAYMENT'
const EXPENSE_PREBILL_APPROVED = 'EXPENSE_PREBILL_APPROVED'
const EXPENSE_PROOF_SUBMITTED = 'EXPENSE_PROOF_SUBMITTED'
const EXPENSE_PROVIDER_REFUND = 'EXPENSE_PROVIDER_REFUND'
const EXPENSE_REFUND = 'EXPENSE_REFUND'
const EXPENSE_SH_SHARE_CARD = 'EXPENSE_SH_SHARE_CARD'
const EXPENSE_SH_SHARE_CHECK = 'EXPENSE_SH_SHARE_CHECK'
const EXPENSE_UNMERGED = 'EXPENSE_UNMERGED'
const INITIAL_SWIPE = 'INITIAL_SWIPE'
const PROVIDER_PAYMENT = 'PROVIDER_PAYMENT'
const EARNED_BENEFIT = 'EARNED_BENEFIT'
const EARNED_DEDUCTIBLE_BENEFIT = 'EARNED_DEDUCTIBLE_BENEFIT'

// these two are legacy (deprecated) expense types but just
// in case they are somewhere in production data, lets handle them
const INITIAL_EXPENSE_PAYMENT = 'INITIAL_EXPENSE_PAYMENT'
const PROOF_SUBMITTED = 'PROOF_SUBMITTED'

const accountTypes = [
  ACCOUNT_BACK_PAYMENT,
  ACCOUNT_CANCELLATION_REFUND,
  ACCOUNT_CREDIT_WITHDRAWAL,
  ACCOUNT_CREDIT_PAYMENT,
  ACCOUNT_CREDIT_FROM_DEDUCTIBLE_TRANSFER,
  ACCOUNT_ENROLLMENT_PAYMENT,
  ACCOUNT_FAILED_PAYMENT,
  ACCOUNT_MONTHLY_PAYMENT,
  ACCOUNT_PAYMENT,
  ACCOUNT_REFUND,
  ACCOUNT_REINSTATEMENT_PAYMENT,
  COVERAGE_CANCELLATION,
  COVERAGE_SUSPENSION,
  INVITATION_CREDIT,
]

const expenseTypes = [
  ADJUSTMENT,
  DEDUCTIBLE_TRANSFER,
  EXPENSE_AUTOPAYMENT,
  EXPENSE_CALCULATION_FAILED,
  EXPENSE_FAILED_PAYMENT,
  EXPENSE_INITIAL_PAYMENT,
  EXPENSE_LATE_FEE,
  EXPENSE_MERGED,
  EXPENSE_PAYMENT,
  EXPENSE_PREBILL_APPROVED,
  EXPENSE_PROOF_SUBMITTED,
  EXPENSE_PROVIDER_REFUND,
  EXPENSE_REFUND,
  EXPENSE_SH_SHARE_CARD,
  EXPENSE_SH_SHARE_CHECK,
  EXPENSE_UNMERGED,
  INITIAL_SWIPE,
  PROVIDER_PAYMENT,
  INITIAL_EXPENSE_PAYMENT,
  PROOF_SUBMITTED,
]

// just to test as we add more that we haven't gotten
// rid of something we need for historical purposes
export const types = [...accountTypes, ...expenseTypes]

/*-----------------------------------------------------------------------*/

/*---- Internal Functions to help display transaction information -------*/

/**
 * handles how we format check details for parsed transactions
 * @param {object} checkDetails
 * @returns {array}
 */
const handleChecks = ({ checkDetails = {} }) => {
  if (isEmpty(checkDetails)) return []

  return [
    `Check #${checkDetails.checkNumber}`,
    `Payable to ${checkDetails.checkPayableTo}`,
    `${checkDetails.checkSendToAddress}`,
    checkDetails.checkUuid,
  ]
}

/**
 * handles how we format dates for parsed transactions
 * @param {string} billingCycleEndDate
 * @param {string} billingCycleStartDate
 * @returns {string}
 */
export const handleDates = ({ billingCycleEndDate, billingCycleStartDate }) => {
  // if there's no start or end date, just return an empty string
  if (!billingCycleEndDate || !billingCycleStartDate) return ''
  return `${formatDateUTC(
    'MMM DL, YYYY',
    billingCycleStartDate,
  )} - ${formatDateUTC('MMM DL, YYYY', billingCycleEndDate)}`
}

/**
 * handles how we format names and expense/policy ids for
 * parsed transactions. this is only used in admin atm
 * @param {obj} transaction
 * @returns {obj}
 */
export const handleLink = (transaction = {}) => {
  const {
    expenseId,
    firstName,
    lastName,
    grouped,
    policyId,
    subTransactions = [],
    type,
  } = transaction

  if (type === ACCOUNT_ENROLLMENT_PAYMENT) {
    if (has('subTransactions')(transaction)) {
      // this needs all members from sub transactions
      // to display the UI correctly with each member,
      // their policy and the amount charged
      return {
        multiMember: subTransactions.map(st => ({
          member: `${st.firstName} ${st.lastName}`,
          policy: policyId ? `Policy ${st.policyId}` : '',
          amount: st.amount,
        })),
      }
    } else {
      // is a single member account enrollment
      return {
        member: `${firstName ? firstName : ''} ${lastName ? lastName : ''}`,
        policy: policyId ? `Policy ${policyId}` : '',
      }
    }
  }

  if (accountTypes.includes(type) && !grouped) {
    return {
      member: `${firstName ? firstName : ''} ${lastName ? lastName : ''}`,
      policy: policyId ? `Policy ${policyId}` : '',
    }
  }

  // since the data model puts the member information in the
  // subTransactions if they are grouped, we need to make sure
  // we get the correct information for those grouped account types
  if (accountTypes.includes(type) && grouped) {
    // the member in the subTransaction should be the same
    // across subTransactions, so we'll just extract the first
    return {
      member: `${subTransactions[0].firstName} ${subTransactions[0].lastName}`,
      policy: `Policy ${policyId}`,
    }
  }

  if (expenseTypes.includes(type) && !grouped) {
    return {
      member: `${firstName ? firstName : ''} ${lastName ? lastName : ''}`,
      expense: expenseId ? `Expense #${expenseId}` : '',
    }
  }

  // since the data model puts the member information in the
  // subTransactions if they are grouped, we need to make sure
  // we get the correct information for those grouped expense types
  if (expenseTypes.includes(type) && grouped) {
    // the member in the subTransaction should be the same
    // across subTransactions, so we'll just extract the first
    return {
      member: `${subTransactions[0].firstName} ${subTransactions[0].lastName}`,
      expense: `Expense #${expenseId}`,
    }
  }
}

/**
 *  handles descriptions, mainly the html, white space and capitalizaiton
 *  issues that may be present in the descriptions
 * @param {object} transaction
 * @returns {string}
 */
const handleDescriptions = (transaction = {}) => {
  const desc = pipe(
    // get the description or default to empty string
    pathOr('', ['description']),

    // strip out any html tags as sometimes with
    // adjustments, description comes in as stringified html

    replace(/(<([^>]+)>)/gi, ''),

    // trim any whitespace from both ends
    trim,

    // capitalize the first letter of the string
    capitalize,
  )(transaction)

  // next let's check for certain types in which
  // we'll have to modify the descriptions
  if (
    equals(transaction.type, ACCOUNT_PAYMENT) &&
    equals(desc.toLowerCase(), 'change coverage adjustment')
  )
    return 'Anything above Sidecar Health account credit will be charged along with your next premium'

  return desc
}

/**
 * handles how we format payment methods for parsed transactions
 * @param {object} paymentMethod
 * @returns {string}
 */

const handlePaymentMethod = ({ paymentMethod = {} }, productType = '') => {
  if (isEmpty(paymentMethod)) return ''

  const isBankOrCard = has('last4')(paymentMethod)
  const isAccountCredit = propEq('type', 'ACCOUNT_CREDIT')(paymentMethod)
  const isAccountCreditTaxable = propEq(
    'type',
    'ACCOUNT_CREDIT_TAXABLE',
  )(paymentMethod)

  if (isGroupProduct(productType)) {
    if (isBankOrCard) {
      return `${paymentMethod.brand || paymentMethod.bank} ${
        paymentMethod.last4
      }`
    }

    if (isAccountCreditTaxable) {
      return 'Earned Benefit'
    }

    if (isAccountCredit) {
      return 'Reimbursement credit'
    }
  } else {
    if (isAccountCredit) {
      return 'account credit'
    }

    if (isBankOrCard) {
      return `${paymentMethod.brand || paymentMethod.bank} ${
        paymentMethod.last4
      }`
    }
  }
}

/**
 * handles how we format payment methods for transactions
 * with an undefined payment method (should be rare)
 * @param {object} paymentMethod
 * @returns {string}
 */
export const handlePayment = ({
  transaction = {},
  undefinedTxt,
  productType = '',
}) => {
  return !either(isEmpty, isNil)(transaction.paymentMethod)
    ? `${handlePaymentMethod(transaction, productType)}`
    : undefinedTxt
}

/**
 * handles how we format providers for parsed transactions
 * @param {object} transaction
 * @returns {string}
 */
// const handleProvider = transaction => {}

/*-----------------------------------------------------------------------*/

/*- Utils used by UI components to help in the display of transactions --*/

// transaction types that should be colored red
export const negative = [
  ACCOUNT_BACK_PAYMENT,
  ACCOUNT_CREDIT_PAYMENT,
  ACCOUNT_ENROLLMENT_PAYMENT,
  ACCOUNT_MONTHLY_PAYMENT,
  ACCOUNT_PAYMENT,
  ACCOUNT_REINSTATEMENT_PAYMENT,
  EXPENSE_AUTOPAYMENT,
  EXPENSE_INITIAL_PAYMENT,
  EXPENSE_PAYMENT,
]

/*
 * descriptions or do not fit the new mock spec, so we can
 * choose to show or not show descriptions based on type
 * @param {string} type
 * @returns {bool}
 */
export const showDescription = type => {
  switch (type) {
    case ACCOUNT_MONTHLY_PAYMENT:
      return false
    default:
      return true
  }
}

/**
 * some account or expense transaction types no value amount
 * associated to them and do not fit the new mock spec, so we
 * can choose to show or not show descriptions based on type
 * @param {string} type
 * @returns {bool}
 */
export const showValue = type => {
  switch (type) {
    case EXPENSE_MERGED:
    case EXPENSE_UNMERGED:
    case ADJUSTMENT:
    case EXPENSE_CALCULATION_FAILED:
    case EXPENSE_PROOF_SUBMITTED:
    case EXPENSE_PREBILL_APPROVED:
    case COVERAGE_SUSPENSION:
    case COVERAGE_CANCELLATION:
      return false
    default:
      return true
  }
}

/*-----------------------------------------------------------------------*/

/*--- Main utility functionality of the file ----------------------------*/

/**
 * This function creates a parsed transaction object from a transaction entity.
 * It's purpose is to create one place where we turn full payment/transaction
 * models into simple props objects so that we can render them easily in the UI.
 * @param {object} transaction a single transaction object
 * @returns {object} a transaction object with only the relevant parsed data
 */
export const parseTransaction = (transaction = {}, productType = '') => {
  // this is the object that will eventually be returned.
  // it's set here with common values for all types.
  // we do not parse date or amount here because how those
  // values get formatted for display can vary.
  const parsed = {
    amount: transaction.amount,
    date: transaction.createdAt,
    description: handleDescriptions(transaction),
    link: handleLink(transaction),
  }

  switch (transaction.type) {
    /*------ Account cases -----------------------------------*/
    case ACCOUNT_BACK_PAYMENT:
      parsed.title = 'Back payment for suspended coverage'
      parsed.info = [`For not paying ${handleDates(transaction)}`]
      break

    case ACCOUNT_CANCELLATION_REFUND:
      parsed.title = 'Cancellation refund'
      parsed.info = [
        handlePayment({
          transaction,
          undefinedTxt: 'Refunded',
        }),
      ]
      break

    case ACCOUNT_CREDIT_FROM_DEDUCTIBLE_TRANSFER:
      parsed.title =
        'Credit received after your deductible was transferred from your previous plan'
      parsed.info = [
        handlePayment({
          transaction,
          undefinedTxt: 'Account credit',
        }),
      ]
      break

    case ACCOUNT_CREDIT_PAYMENT:
      parsed.title = 'Pay off outstanding balance'
      parsed.info = [
        handlePayment({
          transaction,
          undefinedTxt: 'Paid',
        }),
      ]
      break

    case ACCOUNT_CREDIT_WITHDRAWAL:
      parsed.title = 'Account credit withdrawal'
      parsed.info = handleChecks(transaction)
      break

    case ACCOUNT_ENROLLMENT_PAYMENT:
      parsed.title = 'Enrollment payment'
      parsed.info = [
        handleDates(transaction),
        handlePayment({
          transaction,
          undefinedTxt: 'Paid',
        }),
      ]
      break

    case ACCOUNT_FAILED_PAYMENT:
      parsed.title = 'Failed payment'
      parsed.info = [
        transaction.paymentInitiatedAt
          ? `Originally paid on ${formatDateUTC(
              'MMM DL, YYYY',
              transaction.paymentInitiatedAt,
            )} with ${handlePaymentMethod(transaction)}`
          : handlePaymentMethod(transaction),
      ]
      break

    case ACCOUNT_MONTHLY_PAYMENT:
      parsed.title = 'Premium payment'
      parsed.info = [
        handleDates(transaction),
        handlePayment({
          transaction,
          undefinedTxt: 'Paid',
        }),
      ]
      break

    case ACCOUNT_PAYMENT:
      parsed.title = equals(
        pipe(pathOr('', ['description']), toLower())(transaction),
        'change coverage adjustment',
      )
        ? 'Proration for your change in coverage'
        : 'Payment'
      parsed.info = [
        handlePayment({
          transaction,
          undefinedTxt: 'Paid',
        }),
      ]
      break

    case ACCOUNT_REFUND:
      parsed.title = 'Account refund'
      parsed.info = [
        handlePayment({
          transaction,
          undefinedTxt: 'Applied',
        }),
      ]
      break

    case ACCOUNT_REINSTATEMENT_PAYMENT:
      parsed.title = 'Reinstatement payment'
      parsed.info = [handlePaymentMethod(transaction)]
      break

    case COVERAGE_CANCELLATION:
      parsed.title = 'Cancellation'
      parsed.info = []
      break

    case COVERAGE_SUSPENSION:
      parsed.title = 'Suspension'
      parsed.info = []
      break

    case INVITATION_CREDIT:
      parsed.title = 'Invitation credit'
      parsed.info = [
        transaction.invited ? `For inviting ${transaction.invited}` : '',
      ]
      break

    /*--------------------------------------------------------*/

    /*------ Expense cases -----------------------------------*/
    case ADJUSTMENT:
      parsed.title = 'Expense reopened'
      parsed.info = []
      break

    case DEDUCTIBLE_TRANSFER:
      parsed.title = 'Amount applied to deductible from previous plan'
      parsed.info = [
        handlePayment({
          transaction,
        }),
      ]
      break

    case EXPENSE_AUTOPAYMENT:
      parsed.title = 'Expense autopayment'
      parsed.info = [
        handlePayment({
          transaction,
          undefinedTxt: 'Paid',
          productType,
        }),
      ]
      break

    case EXPENSE_CALCULATION_FAILED:
      parsed.title = 'More information requested on expense'
      parsed.info = []
      break

    case EXPENSE_FAILED_PAYMENT:
      parsed.title = 'Failed expense payment'
      parsed.info = [
        transaction.paymentInitiatedAt
          ? `Originally paid on ${formatDateUTC(
              'MMM DL, YYYY',
              transaction.paymentInitiatedAt,
            )} with ${handlePaymentMethod(transaction)}`
          : handlePaymentMethod(transaction),
      ]
      break

    case EXPENSE_INITIAL_PAYMENT:
    case INITIAL_EXPENSE_PAYMENT:
      parsed.title = 'Your estimated portion'
      parsed.info = [
        handlePayment({
          transaction,
          productType,
        }),
      ]
      break

    case EXPENSE_LATE_FEE:
      parsed.title = 'Late fee'
      parsed.info = []
      break

    case EXPENSE_MERGED:
      parsed.title = 'Expenses merged together'
      parsed.info = []
      break

    case EXPENSE_PAYMENT:
      parsed.title = 'Expense payment'
      parsed.info = [
        handlePayment({
          transaction,
          productType,
        }),
      ]
      break

    case EXPENSE_PREBILL_APPROVED:
      parsed.title = `$${
        transaction.amount || 0
      } has been reserved for your upcoming care`
      parsed.info = []
      break

    case EXPENSE_PROOF_SUBMITTED:
    case PROOF_SUBMITTED:
      parsed.title = 'Proof(s) of care submitted'
      parsed.info = []
      break

    case EXPENSE_PROVIDER_REFUND:
      parsed.title = 'Refund received from medical provider'
      parsed.info = []
      break

    case EXPENSE_REFUND:
      parsed.title = 'Credit received'
      parsed.info = [
        handlePayment({
          transaction,
          undefinedTxt: 'Applied',
          productType,
        }),
      ]
      break

    case EXPENSE_SH_SHARE_CARD:
      parsed.title = 'Plan paid provider'
      parsed.info = [
        `Benefit paid ${
          transaction.merchantName ? `to ${transaction.merchantName}` : ''
        }`,
      ]
      break

    case EXPENSE_SH_SHARE_CHECK:
      parsed.title = 'Sidecar Health share paid'
      parsed.info = handleChecks(transaction)
      break

    case INITIAL_SWIPE:
      parsed.title = 'Plan paid provider'
      // TODO: remove until we have this info in the DTO
      // parsed.info = [
      //   `Estimated benefit of $${transaction.amount}${
      //     transaction.merchantName ? ` for ${transaction.merchantName}` : ''
      //   }`,
      // ]
      break

    case PROVIDER_PAYMENT:
      parsed.title = 'Plan paid provider'
      break

    case EXPENSE_UNMERGED:
      parsed.title = 'Unmerged from another expense'
      parsed.info = []
      break

    case EARNED_BENEFIT:
      delete parsed.amount
      parsed.title = `Earned Benefit used for non-covered services adjusted ${formatPriceCents(
        transaction.amount,
      )}`
      break

    case EARNED_DEDUCTIBLE_BENEFIT:
      delete parsed.amount
      parsed.title = `Earned Benefit used for deductible adjusted ${formatPriceCents(
        transaction.amount,
      )}`
      break

    /*--------------------------------------------------------*/

    default:
      parsed.title = !isEmpty(transaction)
        ? `Unknown type: ${transaction.type}`
        : 'Unknown type'
  }

  return parsed
}
/*-----------------------------------------------------------------------*/
